import { useEffect, useState } from "react";
import { admin, SystemSettings as Settings } from "../../api/client";
import { PageHeader, useFlash } from "./common";

function Toggle({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 10, margin: "12px 0", fontWeight: 400 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: "auto", marginTop: 4 }} />
      <span>
        <strong>{label}</strong>
        <div className="muted" style={{ fontSize: 13 }}>{hint}</div>
      </span>
    </label>
  );
}

export default function SystemSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [keywords, setKeywords] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const flash = useFlash(setToast);

  useEffect(() => {
    admin
      .settings()
      .then((s) => {
        setSettings(s);
        setKeywords(s.prefilter_keywords.join(", "));
      })
      .catch((e) => setError(e.message));
  }, []);

  async function save(changes: Partial<Settings>) {
    try {
      const s = await admin.updateSettings(changes);
      setSettings(s);
      setKeywords(s.prefilter_keywords.join(", "));
      flash("Saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  }

  if (error) return <div className="page-error">{error}</div>;
  if (!settings) return <div className="page-empty">Loading…</div>;

  return (
    <div className="page">
      <PageHeader title="System settings" sub="Runtime switches stored in the database. They take effect on the next worker tick." />
      <div className="detail-card">
        <Toggle
          label="Allow self-service sign-up"
          hint="When off, only admins can create accounts (Users page)."
          checked={settings.allow_registration}
          onChange={(v) => save({ allow_registration: v })}
        />
        <Toggle
          label="AI parsing enabled"
          hint="Master switch. When off, mail is still fetched but waits in the queue unparsed. Useful during an API outage or to cap spend."
          checked={settings.ai_enabled}
          onChange={(v) => save({ ai_enabled: v })}
        />
        <Toggle
          label="Keyword prefilter"
          hint="Skip emails that have no PDF/image attachment and none of the keywords below, without calling the AI. Saves most of the token spend on newsletters."
          checked={settings.prefilter_enabled}
          onChange={(v) => save({ prefilter_enabled: v })}
        />
        <label>
          Prefilter keywords (comma-separated, matched case-insensitively in subject + body)
          <textarea rows={4} value={keywords} onChange={(e) => setKeywords(e.target.value)} disabled={!settings.prefilter_enabled} />
        </label>
        <button
          className="btn btn-primary btn-sm"
          disabled={!settings.prefilter_enabled}
          onClick={() => save({ prefilter_keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean) })}
        >
          Save keywords
        </button>
      </div>
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
